import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import UpdateBanner from './components/UpdateBanner';
import { AuthProvider, useAuth } from './lib/auth';
import { AppLayout } from './components/AppLayout';

import LoginPage      from './pages/LoginPage';
import DashboardPage  from './pages/DashboardPage';
import ProjectsPage   from './pages/ProjectsPage';
import TasksPage      from './pages/TasksV2';
import ObjectivesPage from './pages/ObjectivesPage';
import TeamPage       from './pages/TeamPage';
import PlanningPage   from './pages/PlanningPage';
import NotesPage      from './pages/NotesPage';
import LibraryV2      from './pages/LibraryV2';
import MessagesPage   from './pages/MessagesPage';
import MorePage       from './pages/MorePage';
import ArchivesPage   from './pages/ArchivesPage';
import ClientRoadmapPage from './pages/ClientRoadmapPage';
import ASAScriptPage from './pages/ASAScriptPage';
import TrackingPage from './pages/TrackingPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ElevateSchoolPage from './pages/ElevateSchoolPage';
import ClientPortalPage from './pages/ClientPortalPage';
import GuestLoginPage from './pages/GuestLoginPage';
import WidgetCalendarPage from './pages/WidgetCalendarPage';
import SettingsPage from './pages/SettingsPage';
import InstallBanner from './components/InstallBanner';
import DailyProcessPrompt from './components/DailyProcessPrompt';
import { DeliveryDialogHost } from './components/DeliveryDialog';

import './App.css';

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-primary)' }}>
    <div className="shimmer" style={{ width: 50, height: 50, borderRadius: '50%' }} />
  </div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

// Iter 62fh — Les comptes invités n'ont accès QU'à /tasks (et /reglages read-only).
// Toute autre route les redirige vers /tasks.
function GuestFence({ children }) {
  const { user } = useAuth();
  if (user?.role === 'guest') return <Navigate to="/tasks" replace />;
  return children;
}

// Iter 62cv — Auth-only guard WITHOUT AppLayout (no sidebar/bottom-nav).
// Used for standalone PWA views like /widget/calendar that must be fullscreen
// on iOS home-screen add-to-home installations.
function ProtectedBare({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Contributors get redirected to /projects (their landing) — Overview is founder-only
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Iter 62fh — guests → /tasks direct
  if (user.role === 'guest') return <Navigate to="/tasks" replace />;
  return <Protected><DashboardPage /></Protected>;
}

// Founder-only route guard — redirects others to their home. Includes gestionnaire (admin-lite).
function FounderOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'founder' && user.role !== 'gestionnaire') return <Navigate to="/" replace />;
  return <Protected>{children}</Protected>;
}

// Iter 62bp — Strict founder guard (excludes gestionnaire). Used for Elevate School & ASA Script.
function FounderStrictOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'founder') return <Navigate to="/" replace />;
  return <Protected>{children}</Protected>;
}

// Team-only guard (founder + contributor). Clients redirected to home.
function TeamOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'client') return <Navigate to="/" replace />;
  return <Protected>{children}</Protected>;
}

// Notes/Library-accessible: founder + client (contributors also allowed for library)
function AxelOrClient({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Iter 62cu — Notes access opened to EVERY authenticated user (founders,
  // gestionnaires, clients, monteurs). Each user only sees notes they created
  // or that were shared with them (backend `_can_read_note` filter).
  return <Protected>{children}</Protected>;
}

function ClientAllowed({ children, includeContributor = false }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = user.role === 'founder' || user.role === 'gestionnaire' || user.role === 'client' || (includeContributor && user.role === 'contributor');
  if (!allowed) return <Navigate to="/" replace />;
  return <Protected>{children}</Protected>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-center"
          theme="dark"
          toastOptions={{
            style: {
              background: 'var(--bg-surface1)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              fontFamily: 'Lexend',
            },
          }}
        />
        <InstallBanner />
        <DeliveryDialogHost />
        <DailyProcessPrompt />
        <Routes>
          <Route path="/login"      element={<LoginPage />} />
          {/* Iter 62bp — Public client portal (no auth, magic-link token) */}
          <Route path="/portal/:token" element={<ClientPortalPage />} />
          {/* Iter 62fh — Magic-link invité (durée réglable, accès Tâches only) */}
          <Route path="/guest/:token" element={<GuestLoginPage />} />
          <Route path="/"           element={<HomeRoute />} />
          <Route path="/projects"   element={<TeamOnly><GuestFence><ProjectsPage /></GuestFence></TeamOnly>} />
          <Route path="/tasks"      element={<Protected><TasksPage /></Protected>} />
          <Route path="/objectives" element={<FounderOnly><ObjectivesPage /></FounderOnly>} />
          <Route path="/team"       element={<FounderOnly><TeamPage /></FounderOnly>} />
          <Route path="/client-roadmap" element={<FounderOnly><ClientRoadmapPage /></FounderOnly>} />
          <Route path="/planning"   element={<Protected><GuestFence><PlanningPage /></GuestFence></Protected>} />
          <Route path="/notes"      element={<Navigate to="/library" replace />} />
          <Route path="/library"    element={<Protected><GuestFence><LibraryV2 /></GuestFence></Protected>} />
          <Route path="/messages"   element={<TeamOnly><GuestFence><MessagesPage /></GuestFence></TeamOnly>} />
          <Route path="/more"       element={<Protected><GuestFence><MorePage /></GuestFence></Protected>} />
          {/* Iter 62ch — Réglages page (personal user settings hub) */}
          <Route path="/reglages"   element={<Protected><GuestFence><SettingsPage /></GuestFence></Protected>} />
          <Route path="/settings"   element={<Navigate to="/reglages" replace />} />
          <Route path="/archives"   element={<FounderOnly><ArchivesPage /></FounderOnly>} />
          <Route path="/asa-script"  element={<FounderStrictOnly><ASAScriptPage /></FounderStrictOnly>} />
          <Route path="/tracking"   element={<Protected><GuestFence><TrackingPage /></GuestFence></Protected>} />
          <Route path="/clients"    element={<FounderOnly><ClientsPage /></FounderOnly>} />
          <Route path="/clients/:cid" element={<FounderOnly><ClientDetailPage /></FounderOnly>} />
          <Route path="/elevate-school" element={<FounderStrictOnly><ElevateSchoolPage /></FounderStrictOnly>} />
          {/* Iter 62ak / 62cv — Widget calendrier iOS home-screen (PWA fullscreen, sans sidebar) */}
          <Route path="/widget/calendar" element={<ProtectedBare><WidgetCalendarPage /></ProtectedBare>} />
          <Route path="*"           element={<Navigate to="/" replace />} />
        </Routes>
        {/* Iter 62bh — global PWA update banner (checks /api/version every 60s) */}
        <UpdateBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
